import { ReactNode } from 'react';

import type { IProgressBarStyleConfig } from '@appcharge/shared-ui';
import {
  BundlesLayout,
  createGradientOrSolid,
  DefaultDailyBonus,
  DefaultRewardCalendar,
  EBundlesViewModel,
  EFontWeights,
  HugeRollingOffer,
  OnePlusNRollingOffer,
  ProgressBar,
  ProgressBarDesign,
  StoreRefreshPopup
} from '@appcharge/shared-ui';
import {
  EBundlesInternalViewModel,
  ERewardCalendarPopUpTrigger,
  ESpecialOfferInternalViewModel
} from 'constants/enums';
import { renderingUtil } from 'utils/renderingUtil';
import {
  MOCK_PRICES,
  MOCK_PROGRESS_BAR_PRODUCTS,
  MOCK_REWARD_CALENDAR_SEQUENCE,
  MOCK_ROLLING_OFFERS_PRODUCTS,
  POPUP_DEFAULTS,
  PROGRESS_BAR_DEFAULTS,
  PROGRESS_BAR_PREVIEW_DEFAULTS,
  PROMOTION_DEFAULTS,
  REWARD_CALENDAR_DEFAULTS,
  ROLLING_OFFER_DEFAULTS,
  TRIGGERED_POPUP_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import {
  GradientColor,
  RenderCardFn
} from 'views/OfferDesign/OfferDesign.types';
import {
  normalizeBgColor,
  toNumberWithFallback
} from 'views/OfferDesign/OfferDesign.utils';

import { PREVIEW_ELEMENT_ID } from './OfferDesignPreview.consts';

const BUY_ONE_GET_MANY_PREVIEW_INDEX_TO_COLLECT = 0;
const STANDARD_PREVIEW_INDEX_TO_COLLECT = 1;

export const applyCssVariables = (
  el: HTMLDivElement,
  storeScreen: any,
  font: string
) => {
  el.style.setProperty(
    '--button-text-color',
    storeScreen.buttonTextColor || ''
  );
  el.style.setProperty(
    '--button-bg-color',
    createGradientOrSolid(storeScreen.buttonColor) || ''
  );
  el.style.setProperty('--font-family', font || '');
};

export const clearCssVariables = (el: HTMLDivElement) => {
  el.style.removeProperty('--button-text-color');
  el.style.removeProperty('--button-bg-color');
  el.style.removeProperty('--font-family');
};

export const renderBundleCard: RenderCardFn = (
  values,
  generalTheme,
  storeScreen
): ReactNode => {
  const viewModel = generalTheme.bundlesViewModel as EBundlesViewModel;
  const internalModel =
    generalTheme.bundlesInternalViewModel || EBundlesInternalViewModel.MAIN_SUB;

  return (
    <BundlesLayout bundlesViewModel={viewModel} isPreview>
      {renderingUtil.renderBundleByInternalModel(internalModel, {
        index: 0,
        ...(values.backgroundImage
          ? { backgroundSrc: values.backgroundImage }
          : {}),
        ...(values.backgroundColor
          ? { backgroundColor: normalizeBgColor(values.backgroundColor) }
          : {}),
        bundleBorderColor: storeScreen.bundleBorderColor,
        productList: [],
        bundleButtonColor: storeScreen.buttonColor,
        bundleButtonTextColor: storeScreen.buttonTextColor,
        fontFamily: generalTheme.font,
        prices: MOCK_PRICES,
        inMobileMock: true,
        gameName: internalModel
      })}
    </BundlesLayout>
  );
};

export const renderPromotionCard: RenderCardFn = (
  values,
  generalTheme,
  storeScreen
): ReactNode => {
  const internalModel =
    generalTheme.specialOffersInternalViewModel ||
    ESpecialOfferInternalViewModel.HUGE;

  return renderingUtil.renderSpecialOfferByInternalModel(internalModel, {
    title: values.title || '',
    fontSize: values.fontSize || PROMOTION_DEFAULTS.fontSize,
    fontWeight: (values.fontWeight ||
      PROMOTION_DEFAULTS.fontWeight) as EFontWeights,
    textColor: values.fontColor || PROMOTION_DEFAULTS.fontColor,
    subTitle: values.subTitleText || '',
    subTitleSize: values.subTitleFontSize || PROMOTION_DEFAULTS.subFontSize,
    subTitleWeight: (values.subTitleFontWeight ||
      PROMOTION_DEFAULTS.fontWeight) as EFontWeights,
    subTitleColor: values.subTitleFontColor || PROMOTION_DEFAULTS.fontColor,
    ...(values.backgroundImage
      ? { backgroundImage: values.backgroundImage }
      : {}),
    ...(values.backgroundColor
      ? { backgroundColor: normalizeBgColor(values.backgroundColor) }
      : {}),
    borderColor: values.borderColor,
    buttonColor: storeScreen.buttonColor,
    design: 'match',
    prices: MOCK_PRICES,
    productsList: [],
    selectOffer: () => {},
    collectText: values.collectText || PROMOTION_DEFAULTS.collectText
  });
};

export const renderProgressBarCard: RenderCardFn = (values) => {
  return (
    <ProgressBarDesign
      offerId="preview"
      backgroundOpacity={toNumberWithFallback(
        values.backgroundOpacity,
        PROGRESS_BAR_DEFAULTS.backgroundOpacity
      )}
      frameRadius={toNumberWithFallback(
        values.frameRadius,
        PROGRESS_BAR_DEFAULTS.frameRadius
      )}
      barFillColor={
        normalizeBgColor(values.barFillColor) ||
        PROGRESS_BAR_DEFAULTS.barFillColor
      }
      backgroundColor={
        normalizeBgColor(values.barBackgroundColor) ||
        PROGRESS_BAR_DEFAULTS.backgroundColor
      }
      barRadius={toNumberWithFallback(
        values.barRadius,
        PROGRESS_BAR_DEFAULTS.barRadius
      )}
      borderColor={
        normalizeBgColor(values.borderColor) ||
        PROGRESS_BAR_DEFAULTS.borderColor
      }
      borderWidth={toNumberWithFallback(
        values.borderWidth,
        PROGRESS_BAR_DEFAULTS.borderWidth
      )}
      accumulatedItemUrl={values.accumulatedItemImageUrl || ''}
      accumulatedItemBgImageUrl={
        values.accumulatedItemBackgroundImageUrl ||
        PROGRESS_BAR_DEFAULTS.accumulatedItemBackgroundImageUrl
      }
      textColor={values.textColor || PROGRESS_BAR_DEFAULTS.textColor}
      barBackgroundImage={values.barBackgroundImage || ''}
      headerImage={values.headerImage || ''}
      progressBarProducts={MOCK_PROGRESS_BAR_PRODUCTS}
      collectedPoints={PROGRESS_BAR_PREVIEW_DEFAULTS.collectedPoints}
      isPreview
      activeMissionIndex={PROGRESS_BAR_PREVIEW_DEFAULTS.activeMissionIndex}
      missionsLength={PROGRESS_BAR_PREVIEW_DEFAULTS.missionsLength}
    />
  );
};

export const renderProgressBarCardV2: RenderCardFn = (values) => {
  const styleConfig: IProgressBarStyleConfig = {
    backgroundOpacity: toNumberWithFallback(
      values.backgroundOpacity,
      PROGRESS_BAR_DEFAULTS.backgroundOpacity
    ),
    frameRadius: toNumberWithFallback(
      values.frameRadius,
      PROGRESS_BAR_DEFAULTS.frameRadius
    ),
    barFillColor:
      normalizeBgColor(values.barFillColor) ||
      PROGRESS_BAR_DEFAULTS.barFillColor,
    backgroundColor:
      normalizeBgColor(values.barBackgroundColor) ||
      PROGRESS_BAR_DEFAULTS.backgroundColor,
    barRadius: toNumberWithFallback(
      values.barRadius,
      PROGRESS_BAR_DEFAULTS.barRadius
    ),
    borderColor:
      normalizeBgColor(values.borderColor) || PROGRESS_BAR_DEFAULTS.borderColor,
    borderWidth: toNumberWithFallback(
      values.borderWidth,
      PROGRESS_BAR_DEFAULTS.borderWidth
    ),
    textColor: values.textColor || PROGRESS_BAR_DEFAULTS.textColor
  };

  return (
    <ProgressBar
      progress={
        (PROGRESS_BAR_PREVIEW_DEFAULTS.collectedPoints /
          MOCK_PROGRESS_BAR_PRODUCTS.barSize) *
        100
      }
      currentValue={PROGRESS_BAR_PREVIEW_DEFAULTS.collectedPoints}
      maxValue={MOCK_PROGRESS_BAR_PRODUCTS.barSize}
      styleConfig={styleConfig}
      leftImageUrl={values.accumulatedItemImageUrl || ''}
      rightImageUrl={MOCK_PROGRESS_BAR_PRODUCTS.products[0].images.product}
      rightLabel={MOCK_PROGRESS_BAR_PRODUCTS.products[0].quantityDisplay}
      barBackgroundImage={values.barBackgroundImage || ''}
      headerImage={values.headerImage || ''}
      isPreview
    />
  );
};

const DEFAULT_BUY_ONE_GET_MANY_FREE_TILES_COLUMNS = 2;

const buildBuyOneGetManyDesignPreviewProducts = (freeTilesColumns: number) =>
  MOCK_ROLLING_OFFERS_PRODUCTS.slice(0, 1 + freeTilesColumns).map(
    (product, position) => ({ ...product, index: position })
  );

export const renderBuyOneGetManyCard: RenderCardFn = (
  values,
  _generalTheme,
  storeScreen
) => {
  const freeTilesColumns =
    values.freeTilesLayout === undefined
      ? DEFAULT_BUY_ONE_GET_MANY_FREE_TILES_COLUMNS
      : Number(values.freeTilesLayout);

  return (
    <OnePlusNRollingOffer
      isPreview
      isPermanent
      offerId="offer-design-buy-one-get-many-preview"
      indexToCollect={BUY_ONE_GET_MANY_PREVIEW_INDEX_TO_COLLECT}
      backgroundImage={values.backgroundImage}
      headerImage={values.headerImage}
      backgroundOpacity={
        values.backgroundOpacity != null
          ? Number(values.backgroundOpacity)
          : ROLLING_OFFER_DEFAULTS.backgroundOpacity
      }
      backgroundColor={
        normalizeBgColor(values.backgroundColor) ||
        ROLLING_OFFER_DEFAULTS.backgroundColor
      }
      borderColor={
        normalizeBgColor(values.borderColor) ||
        ROLLING_OFFER_DEFAULTS.borderColor
      }
      borderWidth={
        values.borderWidth != null
          ? Number(values.borderWidth)
          : ROLLING_OFFER_DEFAULTS.borderWidth
      }
      collectBehavior="disappear"
      subRollingOfferUiProps={{
        backgroundColor:
          normalizeBgColor(values.offersBackgroundColor) ||
          ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
        backgroundImage: values.offersBackgroundImage || undefined,
        lockImage: values.offersLockImage || ROLLING_OFFER_DEFAULTS.lockImage,
        buttonColor: storeScreen.buttonColor,
        collectText: values.collectText || ROLLING_OFFER_DEFAULTS.collectText,
        buttonTextColor: storeScreen.buttonTextColor
      }}
      selectOffer={() => undefined}
      subRollingOffersProducts={buildBuyOneGetManyDesignPreviewProducts(
        freeTilesColumns
      )}
      variantConfig={{
        kind: 'onePlusN',
        plusImage: values.plusImage,
        freeTilesColumns
      }}
    />
  );
};

export const renderRollingOfferCard: RenderCardFn = (
  values,
  _generalTheme,
  storeScreen
) => {
  const backgroundOpacity =
    values.backgroundOpacity != null
      ? Number(values.backgroundOpacity)
      : ROLLING_OFFER_DEFAULTS.backgroundOpacity;
  const borderWidth =
    values.borderWidth != null
      ? Number(values.borderWidth)
      : ROLLING_OFFER_DEFAULTS.borderWidth;

  const sharedProps = {
    isPreview: true as const,
    isPermanent: true,
    backgroundImage: values.backgroundImage,
    horizontalBackgroundImage: values.horizontalBackgroundImage,
    headerImage: values.headerImage,
    backgroundOpacity,
    backgroundColor:
      normalizeBgColor(values.backgroundColor) ||
      ROLLING_OFFER_DEFAULTS.backgroundColor,
    borderColor:
      normalizeBgColor(values.borderColor) ||
      ROLLING_OFFER_DEFAULTS.borderColor,
    borderWidth,
    collectBehavior: 'disappear' as const,
    subRollingOfferUiProps: {
      backgroundColor:
        normalizeBgColor(values.offersBackgroundColor) ||
        ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
      backgroundImage: values.offersBackgroundImage || undefined,
      lockImage: values.offersLockImage || ROLLING_OFFER_DEFAULTS.lockImage,
      buttonColor: storeScreen.buttonColor,
      collectText: values.collectText || ROLLING_OFFER_DEFAULTS.collectText,
      buttonTextColor: storeScreen.buttonTextColor
    },
    selectOffer: () => undefined
  };

  return (
    <HugeRollingOffer
      {...sharedProps}
      indexToCollect={STANDARD_PREVIEW_INDEX_TO_COLLECT}
      arrowColor={values.arrowColor || ROLLING_OFFER_DEFAULTS.arrowColor}
      arrowImage={values.arrowImage || ''}
      enableScroll={Boolean(values.scrollArrowImage)}
      scrollArrowImage={values.scrollArrowImage || ''}
      subRollingOffersProducts={MOCK_ROLLING_OFFERS_PRODUCTS}
    />
  );
};

export const renderPopupCard: RenderCardFn = (values) => {
  return (
    <DefaultDailyBonus
      isPreview
      title={values.title || ''}
      backgroundImage={values.backgroundImage}
      titleWeight={
        (values.fontWeight || POPUP_DEFAULTS.fontWeight) as EFontWeights
      }
      titleSize={(values.fontSize || POPUP_DEFAULTS.fontSize) + 'px'}
      titleColor={
        values.fontColor?.colorOne || POPUP_DEFAULTS.fontColor.colorOne
      }
      onCollect={async () => true}
      borderColor={
        normalizeBgColor(values.borderColor) || POPUP_DEFAULTS.borderColor
      }
      borderWidth={
        values.borderWidth != null
          ? Number(values.borderWidth)
          : POPUP_DEFAULTS.borderWidth
      }
      modalContainer={() => document.getElementById(PREVIEW_ELEMENT_ID)}
      dataTestId="popup-preview"
    />
  );
};

export const renderRewardCalendarCard: RenderCardFn = (values) => {
  return (
    <DefaultRewardCalendar
      isPreview
      showCloseButton={false}
      dataTestId="reward-calendar-preview"
      buttonText="Collect"
      isAllowedToCollect
      indexToCollect={2}
      productsSequence={MOCK_REWARD_CALENDAR_SEQUENCE}
      rewardCalendar={{
        unlockedIndex: 2,
        nextUnlockAt: new Date(Date.now() + 60 * 60 * 1000),
        popupTrigger: ERewardCalendarPopUpTrigger.SHOW_AUTOMATICALLY as never
      }}
      rewardCalendarUi={{
        headerImage: values.headerImage || '',
        collectibleBadgeImage:
          values.collectibleBadgeImage ||
          REWARD_CALENDAR_DEFAULTS.collectibleBadgeImage,
        lockedBadgeImage:
          values.lockedBadgeImage || REWARD_CALENDAR_DEFAULTS.lockedBadgeImage,
        backgroundColor:
          values.backgroundColor || REWARD_CALENDAR_DEFAULTS.backgroundColor,
        dayFontColor:
          values.dayFontColor || REWARD_CALENDAR_DEFAULTS.dayFontColor,
        subRewardBackgroundImage: values.subRewardBackgroundImage || '',
        mainRewardBackgroundImage: values.mainRewardBackgroundImage || '',
        collectedIconImage:
          values.collectIconImage || REWARD_CALENDAR_DEFAULTS.collectIconImage,
        lockedIconImage:
          values.lockedIconImage || REWARD_CALENDAR_DEFAULTS.lockedIconImage
      }}
      onCollect={() => Promise.resolve(true)}
      modalContainer={() => document.getElementById(PREVIEW_ELEMENT_ID)}
    />
  );
};

export const renderTriggeredPopupCard: RenderCardFn = (values) => {
  const bgColorOne =
    typeof values.backgroundColor === 'object'
      ? (values.backgroundColor as GradientColor).colorOne
      : values.backgroundColor;

  return (
    <StoreRefreshPopup
      open
      disableBodyLock
      onClose={() => {}}
      onCollect={() => {}}
      title={values.title || ''}
      titleColor={
        values.fontColor?.colorOne ||
        TRIGGERED_POPUP_DEFAULTS.fontColor.colorOne
      }
      titleWeight={values.fontWeight || TRIGGERED_POPUP_DEFAULTS.fontWeight}
      backgroundColor={
        bgColorOne || TRIGGERED_POPUP_DEFAULTS.backgroundColor.colorOne
      }
      backgroundImage={values.backgroundImage}
      buttonText={MOCK_PRICES.priceDisplay}
      products={values?.products || []}
      container={document.getElementById(PREVIEW_ELEMENT_ID)}
    />
  );
};
